import {
  CheckCircle2,
  FileJson,
  Image as ImageIcon,
  LayoutTemplate,
  Loader2,
  Upload,
  Wand2,
  X,
} from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { ComponentNode } from "@shared/types";

import { useBuilderStore } from "@/lib/builder/builder-store";
import { importFigmaJson, type FigmaImportResult, type FigmaImportStats } from "@/lib/builder/figma-import";
import { generateAppSdd, getDebugSchema } from "@/services/debugpavilion";
import { fetchFigmaFile, imageToCode, type ImageToCodeResult } from "@/services/design";

import { useAiPanelStore, type AiPanelBridge } from "./ai-panel-store";

type ImportPanel = "image" | "figma" | null;

/** 把组件片段应用到当前活动页面的 layout。 */
function applyFragmentToActivePage(node: ComponentNode): void {
  const builder = useBuilderStore.getState();
  const pageId = builder.activePageId;
  const nextSchema = {
    ...builder.schema,
    pages: builder.schema.pages.map((page) =>
      page.id === pageId ? { ...page, layout: node } : page,
    ),
  };
  builder.setSchema(nextSchema);
}

/** 从 axios 错误中提取后端 detail（string 或 {message}）。 */
function extractApiError(error: unknown): { message: string; unavailable: boolean } {
  const err = error as { response?: { status?: number; data?: { detail?: unknown } } };
  const detail = err.response?.data?.detail;
  if (typeof detail === "string") return { message: detail, unavailable: err.response?.status === 501 };
  if (detail && typeof detail === "object") {
    const message = (detail as { message?: string }).message;
    if (message) return { message, unavailable: err.response?.status === 501 };
  }
  return { message: String(error), unavailable: err.response?.status === 501 };
}

export function DesignMode({ savedAppId, onAppCreated }: AiPanelBridge) {
  const design = useAiPanelStore((state) => state.design);
  const setDesign = useAiPanelStore((state) => state.setDesign);
  const setSchema = useBuilderStore((state) => state.setSchema);
  const [requirement, setRequirement] = React.useState("");

  // 图像转代码
  const [activeImport, setActiveImport] = React.useState<ImportPanel>(null);
  const [imageBusy, setImageBusy] = React.useState(false);
  const [imageResult, setImageResult] = React.useState<ImageToCodeResult | null>(null);
  const [imageError, setImageError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Figma 转换
  const [figmaJson, setFigmaJson] = React.useState("");
  const [figmaUrl, setFigmaUrl] = React.useState("");
  const [figmaToken, setFigmaToken] = React.useState("");
  const [figmaFetching, setFigmaFetching] = React.useState(false);
  const [figmaPreview, setFigmaPreview] = React.useState<FigmaImportResult | null>(null);
  const [figmaError, setFigmaError] = React.useState<string | null>(null);

  const busy = design?.status === "generating";

  const handleGenerate = async () => {
    if (!requirement.trim() || busy) return;
    setDesign({ requirement, status: "generating" });
    try {
      const generation = await generateAppSdd({
        requirement,
        appId: savedAppId ?? undefined,
        sync: false,
      });
      const appId = generation.app_id;
      if (!savedAppId && onAppCreated) onAppCreated(appId);
      // SDD 部分同步完成，schema 已落库，拉取后热更新画布①
      const detail = await getDebugSchema(appId);
      setSchema(detail.app_schema);
      setDesign({ requirement, status: "ready", appId });
    } catch (error) {
      setDesign({ requirement, status: "error", error: String(error) });
    }
  };

  /** 图像转代码：读取文件为 data URL → 调端点 → 应用到画布。 */
  const handleImageFile = (file: File | undefined) => {
    if (!file) return;
    setImageBusy(true);
    setImageError(null);
    setImageResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result ?? "");
      void (async () => {
        try {
          const result = await imageToCode(dataUrl, requirement);
          applyFragmentToActivePage(result.fragment);
          setImageResult(result);
        } catch (error) {
          const { message, unavailable } = extractApiError(error);
          setImageError(unavailable ? "LLM vision 未配置：" + message : message);
        } finally {
          setImageBusy(false);
        }
      })();
    };
    reader.onerror = () => {
      setImageBusy(false);
      setImageError("图片读取失败，请重试");
    };
    reader.readAsDataURL(file);
  };

  /** Figma 转换：粘贴 JSON → 解析预览（组件树统计）。 */
  const handleFigmaParse = () => {
    setFigmaError(null);
    setFigmaPreview(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(figmaJson);
    } catch {
      setFigmaError("Figma JSON 解析失败：不是合法 JSON");
      return;
    }
    try {
      setFigmaPreview(importFigmaJson(parsed));
    } catch (error) {
      setFigmaError(String(error instanceof Error ? error.message : error));
    }
  };

  /** Figma 转换：确认应用到当前页面 schema。 */
  const handleFigmaApply = () => {
    if (!figmaPreview) return;
    applyFragmentToActivePage(figmaPreview.node);
    setFigmaPreview({ ...figmaPreview, applied: true });
  };

  /** Figma 转换：粘贴文件链接 + token，服务端代拉 Figma REST API → 解析预览。 */
  const handleFigmaFetch = async () => {
    if (!figmaUrl.trim() || !figmaToken.trim() || figmaFetching) return;
    setFigmaFetching(true);
    setFigmaError(null);
    setFigmaPreview(null);
    try {
      const result = await fetchFigmaFile(figmaUrl.trim(), figmaToken.trim());
      setFigmaPreview(importFigmaJson(result.data));
    } catch (error) {
      const { message } = extractApiError(error);
      setFigmaError(message);
    } finally {
      setFigmaFetching(false);
    }
  };

  const stats = figmaPreview?.stats;
  const statLabels: Array<[keyof FigmaImportStats, string]> = [
    ["containers", "容器"],
    ["texts", "文本"],
    ["cards", "卡片"],
    ["images", "图片"],
    ["buttons", "按钮"],
    ["skipped", "跳过"],
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-3 overflow-auto p-3">
        <div className="rounded-md bg-muted/50 p-2.5 text-xs text-muted-foreground">
          设计模式：用自然语言描述页面，AI 生成高保真原型（AppSchema），生成后自动回填「画布① 无代码画布」。
        </div>

        {design?.status === "error" && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-xs text-destructive">
            {design.error}
          </div>
        )}

        {busy && (
          <div className="flex items-center gap-2 rounded-md border bg-card p-3 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            正在生成原型 Schema 并回填画布...
          </div>
        )}

        {design?.status === "ready" && !busy && (
          <div
            className="flex items-center gap-2 rounded-md border border-green-500/40 bg-green-500/10 p-2.5 text-xs text-green-600 dark:text-green-400"
            data-testid="design-ready"
          >
            <CheckCircle2 className="h-4 w-4" />
            原型已生成并应用到画布①，可直接拖拽微调。
          </div>
        )}

        <div className="space-y-2 border-t pt-3">
          <div className="text-xs font-semibold text-muted-foreground">更多入口</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setActiveImport(activeImport === "image" ? null : "image")}
              data-testid="design-reserved-image-to-code"
              className="flex flex-col items-center gap-1 rounded-md border border-dashed p-3 text-xs text-muted-foreground hover:border-primary/50 hover:text-foreground"
            >
              <ImageIcon className="h-4 w-4" />
              图像转代码
              <span className="text-[10px]">上传设计稿截图生成组件片段</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveImport(activeImport === "figma" ? null : "figma")}
              data-testid="design-reserved-figma"
              className="flex flex-col items-center gap-1 rounded-md border border-dashed p-3 text-xs text-muted-foreground hover:border-primary/50 hover:text-foreground"
            >
              <LayoutTemplate className="h-4 w-4" />
              Figma 转换
              <span className="text-[10px]">粘贴 Figma JSON 导入设计</span>
            </button>
          </div>
        </div>

        {/* 图像转代码面板 */}
        {activeImport === "image" && (
          <div className="space-y-2 rounded-md border bg-card p-3" data-testid="design-image-panel">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold">图像转代码</div>
              <button
                type="button"
                onClick={() => setActiveImport(null)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="关闭"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="design-image-input"
              onChange={(e) => handleImageFile(e.target.files?.[0])}
            />
            <Button
              type="button"
              size="sm"
              className="w-full"
              disabled={imageBusy}
              onClick={() => fileInputRef.current?.click()}
              data-testid="design-image-upload"
            >
              {imageBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
              {imageBusy ? "生成中..." : "上传设计稿截图"}
            </Button>
            <p className="text-[10px] text-muted-foreground">
              上传截图后调用 /design/image-to-code 生成组件片段并应用到当前页面。
            </p>
            {imageError && (
              <div
                className="rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-xs text-destructive"
                data-testid="design-image-error"
              >
                {imageError}
              </div>
            )}
            {imageResult && (
              <div
                className="space-y-1 rounded-md border border-green-500/40 bg-green-500/10 p-2.5 text-xs text-green-600 dark:text-green-400"
                data-testid="design-image-ready"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  组件片段已生成并应用到画布①。
                </div>
                {imageResult.degraded && (
                  <div className="text-amber-600 dark:text-amber-400" data-testid="design-image-degraded">
                    已降级：LLM vision 未配置，按提示词生成占位布局（无法读取图片内容）。
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground">{imageResult.message}</div>
              </div>
            )}
          </div>
        )}

        {/* Figma 转换面板 */}
        {activeImport === "figma" && (
          <div className="space-y-2 rounded-md border bg-card p-3" data-testid="design-figma-panel">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold">Figma 转换</div>
              <button
                type="button"
                onClick={() => setActiveImport(null)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="关闭"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-1.5">
              <Input
                value={figmaUrl}
                onChange={(e) => setFigmaUrl(e.target.value)}
                placeholder="Figma 文件链接（https://www.figma.com/file/... 或 /design/...）"
                className="h-8 text-xs"
                data-testid="design-figma-url"
              />
              <div className="flex gap-2">
                <Input
                  value={figmaToken}
                  onChange={(e) => setFigmaToken(e.target.value)}
                  placeholder="Figma 个人访问 token（仅单次请求使用，不保存）"
                  type="password"
                  className="h-8 text-xs"
                  data-testid="design-figma-token"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={!figmaUrl.trim() || !figmaToken.trim() || figmaFetching}
                  onClick={() => void handleFigmaFetch()}
                  data-testid="design-figma-fetch"
                >
                  {figmaFetching ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                  拉取
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                填链接 + token 由服务端调 Figma API 拉取节点树；或直接粘贴 JSON：
              </p>
            </div>
            <Textarea
              value={figmaJson}
              onChange={(e) => setFigmaJson(e.target.value)}
              placeholder='粘贴 Figma REST API file/nodes JSON，如 {"document": {"type": "CANVAS", "children": [...]}}'
              className="min-h-[96px] resize-none text-xs font-mono"
              data-testid="design-figma-textarea"
            />
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                className="flex-1"
                disabled={!figmaJson.trim()}
                onClick={handleFigmaParse}
                data-testid="design-figma-preview"
              >
                <FileJson className="mr-1.5 h-4 w-4" />
                解析预览
              </Button>
              <Button
                type="button"
                size="sm"
                variant="default"
                className="flex-1"
                disabled={!figmaPreview}
                onClick={handleFigmaApply}
                data-testid="design-figma-apply"
              >
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                应用到画布
              </Button>
            </div>
            {figmaError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-xs text-destructive" data-testid="design-figma-error">
                {figmaError}
              </div>
            )}
            {figmaPreview && (
              <div className="space-y-1 rounded-md border bg-muted/40 p-2.5 text-xs" data-testid="design-figma-stats">
                <div className="font-semibold">解析结果（组件树统计）</div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground">
                  {statLabels.map(([key, label]) => (
                    <span key={key}>
                      {label} <b className="text-foreground">{stats?.[key] ?? 0}</b>
                    </span>
                  ))}
                </div>
                {figmaPreview.warnings.length > 0 && (
                  <ul className="list-inside list-disc text-[10px] text-muted-foreground">
                    {figmaPreview.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                )}
                {figmaPreview.applied && (
                  <div className="text-green-600 dark:text-green-400" data-testid="design-figma-applied">
                    ✔ 已应用到当前页面 schema。
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t bg-card p-3">
        <Textarea
          value={requirement}
          onChange={(e) => setRequirement(e.target.value)}
          placeholder="例如：做一个深色主题的客户管理页，左侧导航 + 顶部搜索 + 客户卡片列表..."
          className="min-h-[64px] resize-none text-sm"
        />
        <Button
          className="mt-2 w-full"
          size="sm"
          disabled={busy || !requirement.trim()}
          onClick={() => void handleGenerate()}
          data-testid="design-generate"
        >
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1.5 h-4 w-4" />}
          {busy ? "生成中..." : "生成高保真原型"}
        </Button>
      </div>
    </div>
  );
}
